// Everything on a TVG page below the hero or banner fades up as it scrolls into view, and the hero's own content as the page opens.
const TvgReveal = {
  init() {
    const main = document.querySelector<HTMLElement>('.tvg-site .global-main')

    if (!main || !('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return
    }

    const targets: HTMLElement[] = []
    const groups = new Map<Element, HTMLElement[]>()

    // A list reveals its items one by one as they scroll into view. Anything marked to stagger reveals its children
    // in turn, all at once, as soon as it shows.
    const parts = (element: Element): HTMLElement[] => {
      if (element.matches('ul, ol') && element.children.length > 1) {
        return Array.from(element.children).flatMap(parts)
      }

      if (!element.hasAttribute('data-tvg-reveal-stagger')) {
        return element instanceof HTMLElement ? [element] : []
      }

      const grouped = Array.from(element.children).flatMap(parts)

      if (!element.parentElement?.closest('[data-tvg-reveal-stagger]')) {
        groups.set(element, grouped)
      }

      return grouped
    }

    // The hero marks what in it reveals, and it reveals first.
    const opening = Array.from(main.querySelectorAll('.tvg-hero [data-tvg-reveal-stagger]'))
      .filter((element) => !element.parentElement?.closest('[data-tvg-reveal-stagger]'))
      .flatMap(parts)

    targets.push(...opening)

    // A block's wrapper holds its parts, so each part reveals on its own.
    main.querySelectorAll<HTMLElement>('.wrapper').forEach((wrapper) => {
      if (wrapper.closest('.tvg-hero, .tvg-banner, dialog') || targets.some((target) => target.contains(wrapper))) {
        return
      }

      Array.from(wrapper.children).forEach((child) => {
        const children = child.classList.contains('content-grid') ? Array.from(child.children) : [child]

        targets.push(...children.flatMap(parts))
      })
    })

    // A part reveals once it is a little way into the viewport.
    const inset = 0.2
    const reveal: IntersectionObserverCallback = (entries, observer) => {
      // What comes into view together reveals in order, a moment apart.
      entries
        .filter((entry) => entry.isIntersecting)
        .flatMap((entry) => {
          observer.unobserve(entry.target)
          return groups.get(entry.target) ?? [entry.target as HTMLElement]
        })
        .forEach((target, index) => {
          target.style.setProperty('--tvg-reveal-delay', `${Math.min(index, 8) * 100}ms`)
          target.classList.add('is-revealed')
        })
    }
    const observer = new IntersectionObserver(reveal, { rootMargin: `0px 0px -${inset * 100}% 0px` })
    // The end of the page never scrolls past the inset, so what sits there reveals as soon as it shows.
    const endObserver = new IntersectionObserver(reveal)

    targets.forEach((target) => {
      target.classList.add('tvg-reveal')

      // Once in place the part drops the transition, so it keeps its own transitions and transform, and says so.
      target.addEventListener('transitionend', function done(event) {
        if (event.target !== target || event.propertyName !== 'transform' || !target.classList.contains('is-revealed')) {
          return
        }

        target.classList.remove('tvg-reveal', 'is-revealed')
        target.style.removeProperty('--tvg-reveal-delay')
        target.removeEventListener('transitionend', done)
        target.dispatchEvent(new CustomEvent('tvg-revealed'))
      })
    })

    // In development the stylesheet arrives after this script, and a part revealed before it would not animate.
    const started = performance.now()
    const observe = () => {
      if (targets.length && window.getComputedStyle(targets[0]).opacity !== '0' && performance.now() - started < 3000) {
        window.requestAnimationFrame(observe)
        return
      }

      // The hero's content reveals as the page opens, wherever the page loader has it.
      opening.forEach((target, index) => {
        target.style.setProperty('--tvg-reveal-delay', `${index * 100}ms`)
        target.classList.add('is-revealed')
      })

      const pageBottom = main.getBoundingClientRect().bottom
      // A group is watched as a whole, in the place of its parts.
      const watched = new Set(targets
        .filter((target) => !opening.includes(target))
        .map((target) => Array.from(groups.keys()).find((group) => group.contains(target)) ?? target))

      watched.forEach((target) => {
        if (pageBottom - target.getBoundingClientRect().top < window.innerHeight * inset) {
          endObserver.observe(target)
        } else {
          observer.observe(target)
        }
      })
    }

    observe()
  },
}

export default TvgReveal
